/**
 * Place Vertex — pick up a line in the middle and bend it.
 *
 * The manual (Ch. 3): "position the cursor on a line you've just placed and
 * click on it — now moving the cursor moves the endpoint and pulls the line
 * (now actually two lines) along behind it." A first click splits the line at
 * the clicked point into two segments sharing a movable vertex; the vertex
 * follows the cursor until a second click nails it. Escape abandons the bend
 * and restores the original line.
 */

import type { LineEntity, PadEntity } from '@sircad/core/model.ts';
import { newId } from '@sircad/core/model.ts';
import type { Point } from '../render/viewport.ts';
import { orthoConstrain, snap } from '../render/viewport.ts';
import type { Tool, ToolContext } from './tool.ts';
import { hitTest, selectable } from './hit.ts';
import { cullEmptyStubs, stubOnPin } from './select.ts';

export class VertexTool implements Tool {
  readonly id = 'vertex';
  readonly label = 'Vertex';
  readonly key = 'e';
  readonly hint = 'Click a line to bend it, click again to set the corner, Esc to cancel';
  readonly cursor = 'crosshair';

  /** The two segments created by the split; their shared point is the vertex. */
  private seg1: LineEntity | null = null;
  private seg2: LineEntity | null = null;

  onPointerDown(p: Point, ctx: ToolContext): void {
    if (this.seg1) { this.nail(ctx); return; }

    // Bend the line on the layer being worked on, for the same reason Grab
    // prefers it: stacked copper otherwise hands you the wrong track.
    const hit = hitTest(ctx.doc(), p.x, p.y, ctx.grid || 0.01, ctx.activeLayer);
    if (!hit || hit.kind !== 'line') {
      ctx.setMessage('Vertex — click on a line');
      return;
    }

    // Split the line at the point on it nearest the click.
    const v = projectOnSegment(p.x, p.y, hit);
    ctx.beginEdit('Bend line');

    this.seg1 = { ...structuredClone(hit), id: newId(), x2: v.x, y2: v.y };
    this.seg2 = { ...structuredClone(hit), id: newId(), x1: v.x, y1: v.y };

    const d = ctx.doc();
    d.entities = d.entities.filter((e) => e.id !== hit.id);
    d.entities.push(this.seg1, this.seg2);
    ctx.redraw();
  }

  onPointerMove(p: Point, ctx: ToolContext): void {
    if (!this.seg1 || !this.seg2) return;
    const v = { x: snap(p.x, ctx.grid), y: snap(p.y, ctx.grid) };

    // Ortho snap is observed by Place Vertex as well as Place Line (manual
    // ch.4). The bend is measured from the segment's fixed far end — the corner
    // it grows out of — so the leading segment lands on a 90°/45° axis exactly
    // as it would if you were drawing it.
    if (ctx.ortho) {
      const a = orthoConstrain(this.seg1.x1, this.seg1.y1, v.x, v.y, ctx.ortho);
      v.x = snap(a.x, ctx.grid);
      v.y = snap(a.y, ctx.grid);
    }

    this.seg1.x2 = v.x; this.seg1.y2 = v.y;
    this.seg2.x1 = v.x; this.seg2.y1 = v.y;
    ctx.commit();
  }

  onKey(ev: KeyboardEvent, ctx: ToolContext): boolean {
    if (this.seg1 && ev.key === 'Enter') { this.nail(ctx); return true; }
    return false;
  }

  cancel(ctx: ToolContext): void {
    if (this.seg1) {
      // Restore the pre-split line — the bend is abandoned.
      ctx.rollback();
      this.seg1 = null;
      this.seg2 = null;
      ctx.redraw();
    }
  }

  onDeactivate(ctx: ToolContext): void {
    this.cancel(ctx);
  }

  private nail(ctx: ToolContext): void {
    this.seg1 = null;
    this.seg2 = null;
    ctx.setMessage('Vertex placed');
    ctx.redraw();
  }
}

/** Nearest point on segment `s` to (px,py), clamped to the segment. */
function projectOnSegment(px: number, py: number, s: LineEntity): Point {
  const dx = s.x2 - s.x1;
  const dy = s.y2 - s.y1;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return { x: s.x1, y: s.y1 };
  let t = ((px - s.x1) * dx + (py - s.y1) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return { x: s.x1 + t * dx, y: s.y1 + t * dy };
}

/**
 * Grab Vertex — pick up a corner that is already there and move it.
 *
 * Distinct from {@link VertexTool}, which *makes* a corner by splitting a
 * straight line. This one takes the nearest existing endpoint and carries it,
 * bringing every other endpoint sitting on the same spot along with it — which
 * is what keeps a polyline joined and a track attached to its neighbour instead
 * of tearing a gap open at the bend.
 *
 * A free pad on the corner — a signal dot, on a schematic — travels with it.
 * The original's own point-grabber says so in its header: find a free pad,
 * "attach the pad, then scan for and attach all line endpoints on all layers".
 * A dot means *these wires join here*, so a corner that walks out from under
 * its dot has turned a junction into a crossover without anyone saying to.
 *
 * A *component* pad does the opposite: it stays, and the corner leaves a stub
 * joining it to where the corner has gone. That is the first of the same
 * header's three cases and {@link stubOnPin} carries it out — a pin belongs to
 * its part and cannot be dragged out of it, but the wire that was on it must
 * not come off it either.
 */
export class GrabVertexTool implements Tool {
  readonly id = 'grabvertex';
  readonly label = 'Grab Vertex';
  readonly key = 'k';
  readonly hint = 'Click a corner to pick it up, move it, click to drop · Esc puts it back';
  readonly cursor = 'crosshair';

  /** Which end of which line is being carried. Several move as one. */
  private ends: { line: LineEntity; end: 1 | 2 }[] = [];
  /** Free pads on the corner — signal dots — carried along with it. */
  private pads: PadEntity[] = [];
  /** Stubs left on the pins the corner has walked off. */
  private stubs: LineEntity[] = [];
  /** Where the corner was picked up, so Escape can put it back. */
  private from: Point | null = null;
  private held = false;
  private moved = false;

  onPointerDown(p: Point, ctx: ToolContext): void {
    if (this.held) { this.drop(ctx); return; }

    const found = pointNear(ctx, p);
    if (!found) {
      ctx.setMessage('Grab Vertex — no corner there');
      return;
    }
    this.ends = found.ends;
    this.pads = found.pads;
    this.from = { x: found.at.x, y: found.at.y };
    this.held = true;
    this.moved = false;
    // One point moves, whatever meets there. The count is said out loud because
    // it tells you how many segments will follow the corner — the rest of the
    // line, and every other vertex on it, stays exactly where it is.
    const dot = found.pads.length > 0 ? ' and its dot' : '';
    ctx.setMessage(
      found.ends.length === 1
        ? `Carrying one end${dot} — click or Space to drop, Esc puts it back`
        : `Carrying a corner where ${found.ends.length} ends meet${dot} — click or Space to drop`);
    ctx.redraw();
  }

  onPointerMove(p: Point, ctx: ToolContext): void {
    if (!this.held || !this.from) return;
    const x = snap(p.x, ctx.grid);
    const y = snap(p.y, ctx.grid);

    const at = this.where();
    if (!at || (at.x === x && at.y === y)) return;

    if (!this.moved) {
      ctx.beginEdit('Move vertex');
      this.moved = true;
      // The corner is still where it was picked up, so this is the moment the
      // pin under it can be found. One stub per corner, as the original makes
      // one for the first line end it grabs there.
      const like = this.ends[0]?.line;
      const stub = like ? stubOnPin(ctx.doc(), at.x, at.y, like) : null;
      if (stub) { ctx.doc().entities.push(stub); this.stubs.push(stub); }
    }
    this.put(x, y);
    ctx.commit();
  }

  /** Where the carried corner is standing now. */
  private where(): Point | null {
    const first = this.ends[0];
    if (first) {
      return first.end === 1
        ? { x: first.line.x1, y: first.line.y1 }
        : { x: first.line.x2, y: first.line.y2 };
    }
    const pad = this.pads[0];
    return pad ? { x: pad.x, y: pad.y } : null;
  }

  /** Move everything the corner holds to one point. */
  private put(x: number, y: number): void {
    for (const e of this.ends) {
      if (e.end === 1) { e.line.x1 = x; e.line.y1 = y; }
      else { e.line.x2 = x; e.line.y2 = y; }
    }
    for (const pad of this.pads) { pad.x = x; pad.y = y; }
    // The stub's other end is nailed to the pin and stays there.
    for (const s of this.stubs) { s.x2 = x; s.y2 = y; }
  }

  holding(): boolean {
    return this.held;
  }

  /** A corner in hand is in progress. */
  busy(): boolean {
    return this.held;
  }

  /**
   * Abandon the move and put the corner back where it was picked up.
   *
   * Escape means "as you were", so a corner that has been dragged halfway
   * across the board returns to the joint it came from — taking every line end
   * that was moving with it, or the bend would come apart.
   */
  cancel(ctx: ToolContext): void {
    if (this.moved && this.from) {
      this.put(this.from.x, this.from.y);
      // Back on the pin it came off, the stub has no length left to have.
      cullEmptyStubs(ctx.doc(), this.stubs);
      ctx.commit();
    }
    this.held = false;
    this.ends = [];
    this.pads = [];
    this.stubs = [];
    this.from = null;
    this.moved = false;
    ctx.redraw();
  }

  onDeactivate(ctx: ToolContext): void {
    this.cancel(ctx);
  }

  private drop(ctx: ToolContext): void {
    cullEmptyStubs(ctx.doc(), this.stubs);
    this.held = false;
    this.ends = [];
    this.pads = [];
    this.stubs = [];
    this.from = null;
    this.moved = false;
    ctx.setMessage('Corner dropped');
    ctx.redraw();
  }
}

/** Everything a single corner holds: line ends, and any free pad on top of it. */
export interface CornerGrab {
  at: Point;
  ends: { line: LineEntity; end: 1 | 2 }[];
  pads: PadEntity[];
}

/**
 * The nearest corner to `p`, and everything standing on it.
 *
 * A free pad — a signal dot — is looked for first and wins on distance ties,
 * which is the original's order too: it scans for a coincident pad before it
 * scans for line ends, and a dot is drawn on top of the wires it joins, so it
 * is what you are pointing at when you point at one. Then every line end
 * coincident with that point comes along, on any layer, so a bend shared by
 * three wires and a dot moves as the single junction it looks like.
 *
 * Free pads only: a component's pin belongs to the part, and Grab Vertex has no
 * business dragging a pin out of a footprint.
 */
function pointNear(ctx: ToolContext, p: Point): CornerGrab | null {
  const doc = ctx.doc();
  const reach = (ctx.grid || 0.01) * 1.5;
  let best: { x: number; y: number } | null = null;
  let bestD = Infinity;

  for (const e of doc.entities) {
    if (!selectable(doc, e)) continue;
    if (ctx.pickFilter && !ctx.pickFilter(e)) continue;
    if (e.kind === 'pad') {
      const d = Math.hypot(e.x - p.x, e.y - p.y);
      // `<=`, so a dot sitting exactly on the corner it marks is preferred to
      // the wire ends underneath it.
      if (d <= bestD && d <= reach) { bestD = d; best = { x: e.x, y: e.y }; }
      continue;
    }
    if (e.kind !== 'line') continue;
    for (const v of [{ x: e.x1, y: e.y1 }, { x: e.x2, y: e.y2 }]) {
      const d = Math.hypot(v.x - p.x, v.y - p.y);
      if (d < bestD && d <= reach) { bestD = d; best = v; }
    }
  }
  if (!best) return null;

  const EPS = 1e-6;
  const on = (x: number, y: number): boolean =>
    Math.abs(x - best!.x) < EPS && Math.abs(y - best!.y) < EPS;

  const out: CornerGrab = { at: best, ends: [], pads: [] };
  for (const e of doc.entities) {
    if (!selectable(doc, e)) continue;
    if (ctx.pickFilter && !ctx.pickFilter(e)) continue;
    if (e.kind === 'pad') {
      if (on(e.x, e.y)) out.pads.push(e);
      continue;
    }
    if (e.kind !== 'line') continue;
    if (on(e.x1, e.y1)) out.ends.push({ line: e, end: 1 });
    if (on(e.x2, e.y2)) out.ends.push({ line: e, end: 2 });
  }
  return out.ends.length === 0 && out.pads.length === 0 ? null : out;
}
